// The Sell point of sale: the unified mixed-basket order, its tenders and its ledger settlement
// (F-POS-09 Sell POS, F-POS-01 order lines/mixed basket, F-POS-12 split tender; spec/06 §8, §9,
// spec/13 §4).
//
// A **sale** is one cart for everything a venue sells (spec/13 §4): a pass and two colas in one
// order. Its lines mix kinds (spec/06 §8.1) — `time` (a pass or PAYG settlement), `food`, `retail`
// and `other` — each priced with its GST components that add back to the price (spec/06 §10.1), and
// the sale's total is the sum. Food and retail lines name a product on the venue's effective menu and
// are priced from it; time and other lines carry their own name and unit price (the pass and
// rate-card catalogues that will price them are the follow-on, spec/06 §3–§4).
//
//   POST …/venues/{v}/sales                 create a sale of mixed lines, priced and placed (F-POS-01)
//   GET  …/venues/{v}/sales                 the venue's sales, newest first (F-POS-15)
//   GET  …/venues/{v}/sales/{id}            one sale, with its lines, tenders and settlement
//   POST …/venues/{v}/sales/{id}/payments   take a tender; split across several is allowed (F-POS-12)
//   POST …/venues/{v}/sales/{id}/cancel     cancel a sale before any payment settles
//
// Money is an integer minor unit plus an ISO code everywhere; the server computes every amount
// (spec/13 §4). On the payment that covers the total, the sale settles to the ledger — sales revenue
// by line kind, GST to `tax_payable` by component, and the tenders to the drawer and the day
// (spec/06 §2.3). Cash and counter UPI settle immediately; online, wallet and on-account tenders are
// the follow-on (spec/06 §9.1).
import { createRoute } from '@hono/zod-openapi';
import { z } from "../zod.js";
import { PassProductIdSchema, PlayerIdSchema, ProductIdSchema, ProductModifierIdSchema, ProductVariantIdSchema, PromotionIdSchema, SaleDocumentIdSchema, SaleIdSchema, SaleLineIdSchema, SalePaymentIdSchema, SaleRefundIdSchema, SessionIdSchema, StationIdSchema, CreditNoteIdSchema, TaxCategoryIdSchema, } from "../ids/schema.js";
import { GstinSchema } from "../primitives/gstin.js";
import { commonProblemResponses, jsonResponse, problemResponse } from "../openapi/responses.js";
import { MoneySchema, NonNegativeMoneySchema } from "../primitives/money.js";
import { BasisPointsSchema, IsoDateTimeSchema } from "../primitives/scalars.js";
import { VenueScopeParamsSchema } from "../primitives/scope.js";
import { PERMISSION_EXTENSION } from "./audit.js";
import { IdempotencyHeaderSchema } from "./id-checks.js";
import { ORDERS_CREATE, ORDERS_DISCOUNT, ORDERS_READ, ORDERS_REFUND, ORDERS_REFUND_APPROVE, ORDERS_VOID, } from "../permissions/index.js";
// ---------------------------------------------------------------------------------------------
// Shared vocabulary (spec/06 §8.1, §9.1, §10.3)
// ---------------------------------------------------------------------------------------------
/**
 * A sale line's kind (spec/06 §8.1). The mixed basket the Sell screen builds: `time` (a pass or a
 * pay-as-you-go settlement), `food` and `retail` (a product on the menu) and `other`. It decides the
 * sales-revenue account the line settles to (spec/06 §2.1): `time` → `sales_play`, `food` →
 * `sales_fnb`, `retail` → `sales_retail`, `other` → `sales_other`.
 */
export const SaleLineKindSchema = z
    .enum(['time', 'food', 'retail', 'other'])
    .openapi('SaleLineKind', { description: 'What a sale line sells (spec/06 §8.1).' });
/**
 * A tender against a sale (spec/06 §9.1), each of which settles immediately: `cash` (into the
 * shift's drawer), `counter_card` (the venue's own card machine: at most the last 4 digits and the slip's
 * reference are recorded, never a card number; F-PAY-02), `counter_upi` (the venue's own QR, recorded once staff
 * confirm the UPI app approved it) and `wallet` (drawn from the sale's player's
 * wallet — bonus and cash in `wallet.spendOrder` — the moment the payment is taken; F-WAL-04). A
 * `wallet` tender needs a sale with a player and an organisation with `wallet.enabled`. As a refund
 * tender, `wallet` credits the player's wallet cash (spec/06 §8.7). Online is the follow-on;
 * `on_account` is taken through its own door.
 */
export const SaleTenderSchema = z
    .enum(['cash', 'counter_card', 'counter_upi', 'wallet'])
    .openapi('SaleTender', { description: 'An immediate tender against a sale (spec/06 §9.1).' });
/**
 * Where a sale refund's money goes (spec/06 §8.7): an immediate tender, or `online` — back to the online payment
 * that paid the sale, through the payment provider (F-PAY-07, ADR-0061). An `online` refund is only for a sale
 * the provider was paid for, up to what it was paid online less what was already refunded online; the provider
 * pays it back to the player's UPI, card or bank, and the commission it carried comes back pro rata.
 */
export const SaleRefundTenderSchema = z
    .enum(['cash', 'counter_card', 'counter_upi', 'wallet', 'online'])
    .openapi('SaleRefundTender', { description: 'Where a sale refund’s money goes (spec/06 §8.7).' });
/**
 * How a sale payment settled (spec/06 §9.1). The immediate counter tenders, plus `on_account` — the
 * checkout tender that creates a receivable rather than taking money now (F-POS-07, spec/06 §8.6).
 * `on_account` is taken through its own gated door (`…/sales/{id}/on-account`), not `…/payments`.
 * `online` is **recorded, never posted**: it appears only when the payment provider captured a payment
 * intent for the sale (`…/sales/{id}/payment-intents`), reported by its webhook or the reconciliation
 * job (spec/06 §9.3, F-PAY-03). The counter cannot take it through `…/payments`.
 */
export const SalePaymentTenderSchema = z
    .enum(['cash', 'counter_card', 'counter_upi', 'wallet', 'on_account', 'online'])
    .openapi('SalePaymentTender', { description: 'How a sale payment settled (spec/06 §9.1).' });
/**
 * Where a sale is in its lifecycle (spec/06 §8.2). `placed` and locked; `partially_paid` once a
 * payment settles and a balance remains; `paid` once payments cover the total; `partially_refunded`
 * and `refunded` once refunds reverse part or all of what was paid; `cancelled` before any payment.
 * The paid and refunded states are derived from settled payments and refunds, never set by an
 * endpoint (spec/06 §8.2, §2.4 invariant 5).
 */
export const SaleStatusSchema = z
    .enum(['placed', 'partially_paid', 'paid', 'partially_refunded', 'refunded', 'cancelled'])
    .openapi('SaleStatus', { description: "A sale's state (spec/06 §8.2)." });
/** Where a sale was placed (spec/06 §8.2). The counter today; kiosk and app orders ride in later. */
export const SaleChannelSchema = z
    .enum(['counter', 'kiosk', 'app', 'web', 'api'])
    .openapi('SaleChannel', { description: 'Where a sale was placed (spec/06 §8.2).' });
/** The venue's tax mode a sale was placed under, stored on it for ever after (spec/06 §10.3 rule 3). */
export const SaleTaxModeSchema = z
    .enum(['regular', 'composition', 'unregistered'])
    .openapi('SaleTaxMode', { description: "The venue's tax mode the sale was placed under (spec/06 §10.3)." });
/** One GST component of a line's tax (spec/06 §10.1): CGST + SGST intra-state, IGST inter-state. */
export const SaleTaxComponentSchema = z
    .object({
    component: z.enum(['CGST', 'SGST', 'IGST']),
    rateBps: BasisPointsSchema,
    amount: MoneySchema,
})
    .openapi('SaleTaxComponent', { description: "A line's GST component (spec/06 §10.1)." });
const NAME = z.string().min(1).max(120);
const NOTE = z.string().min(1).max(2000);
// ---------------------------------------------------------------------------------------------
// A sale (spec/06 §8)
// ---------------------------------------------------------------------------------------------
/** One modifier chosen on a line, snapshot with its price when the sale was placed (spec/06 §8.1). */
export const SaleLineModifierSchema = z
    .object({ name: NAME, price: NonNegativeMoneySchema })
    .openapi('SaleLineModifier');
/** One line of a sale, with its computed value and tax (spec/06 §8.1). */
export const SaleLineSchema = z
    .object({
    id: SaleLineIdSchema,
    kind: SaleLineKindSchema,
    productId: ProductIdSchema.nullable(),
    variantId: ProductVariantIdSchema.nullable(),
    /** The pass product a `time` line sells (F-POS-01, F-PRC-01) — its holding is issued once the sale is paid; null otherwise. */
    passProductId: PassProductIdSchema.nullable(),
    name: NAME,
    quantity: z.int().min(1).max(999),
    unitPrice: NonNegativeMoneySchema,
    modifiers: z.array(SaleLineModifierSchema),
    taxRateBps: BasisPointsSchema,
    /** The line's taxable value and tax, gross (before any promotion), as priced when placed. */
    taxable: MoneySchema,
    tax: MoneySchema,
    total: MoneySchema,
    /** The share of an automatic promotion allocated to this line, pro rata by total (F-POS-03). */
    discount: NonNegativeMoneySchema,
    taxComponents: z.array(SaleTaxComponentSchema),
    /**
     * The tax category the line was sold under (F-PAY-11): the product's, or the venue's default for a custom
     * line; null for a pass line (untaxed) or a custom line priced at the standard rate with no category set.
     */
    taxCategoryId: TaxCategoryIdSchema.nullable(),
    /** The HSN or SAC code printed on the tax invoice's line, snapshot from its category; null when none. */
    hsnSac: z.string().nullable(),
})
    .openapi('SaleLine');
/** One tender against a sale (spec/06 §9.2). A payment's amount is never edited (a correction is a refund). */
/** A cash payment's change credited to a player's wallet (ADR-0036, ADR-0049). */
export const SaleChangeToWalletSchema = z
    .object({
    amount: NonNegativeMoneySchema,
    playerId: PlayerIdSchema,
})
    .openapi('SaleChangeToWallet');
export const SalePaymentSchema = z
    .object({
    id: SalePaymentIdSchema,
    tender: SalePaymentTenderSchema,
    /** What was applied to the sale (≤ the outstanding amount when it was taken). */
    amount: NonNegativeMoneySchema,
    /** For cash: what the player handed over, so change can be shown; null for the exact tenders. */
    tendered: NonNegativeMoneySchema.nullable(),
    /** For cash: `tendered − amount`, the change due. */
    change: NonNegativeMoneySchema.nullable(),
    /**
     * For cash whose change was credited to a player's wallet instead of handed back (ADR-0036, ADR-0049): the change
     * and the player. `tendered` and `change` stay null then (the drawer kept every rupee), so the cash received is
     * `amount + changeToWallet.amount`. Absent for every other payment, and for one taken before this was recorded.
     */
    changeToWallet: SaleChangeToWalletSchema.optional(),
    reference: z.string().min(1).max(120).nullable(),
    status: z.enum(['succeeded']),
    createdAt: IsoDateTimeSchema,
})
    .openapi('SalePayment');
/**
 * What a discount is (spec/06 §8.4): a `code` an organisation defined and staff entered (F-POS-02),
 * or a `manual` staff discount capped by the member's role limit (F-POS-05). Its `amount` is what it
 * took off the sale, computed by the server and stored on the sale as a contra-revenue line at
 * settlement (spec/06 §2.1 `discounts`).
 */
export const SaleDiscountSchema = z
    .object({
    kind: z.enum(['code', 'manual']),
    /** The code entered, upper-cased; null for a manual discount. */
    code: z.string().min(1).max(24).nullable(),
    type: z.enum(['percent', 'amount']),
    /** For a manual discount, the reason the member gave (spec/06 §8.4); null for a code. */
    reason: z.string().min(1).max(500).nullable(),
    amount: NonNegativeMoneySchema,
})
    .openapi('SaleDiscount');
/** One line named by a refund: a sale line and how many of it were returned (spec/06 §8.7). */
export const SaleRefundLineSchema = z
    .object({ saleLineId: SaleLineIdSchema, quantity: z.int().min(1).max(999) })
    .openapi('SaleRefundLine');
/**
 * A refund against a sale (spec/06 §8.7, F-POS-08). `pending_approval` while it waits for a second
 * member above the threshold; `succeeded` once it settles to the ledger and money leaves the tender;
 * `declined` if the approver refuses it. Partial refunds are normal.
 */
export const SaleRefundSchema = z
    .object({
    id: SaleRefundIdSchema,
    status: z.enum(['pending_approval', 'succeeded', 'declined']),
    tender: SaleRefundTenderSchema,
    amount: NonNegativeMoneySchema,
    reason: z.string().min(1).max(500),
    /** The lines it returned, or empty for an amount-only refund (session charges, top-ups). */
    lines: z.array(SaleRefundLineSchema),
    requestedAt: IsoDateTimeSchema,
    settledAt: IsoDateTimeSchema.nullable(),
})
    .openapi('SaleRefund');
/** One automatic promotion applied to a sale, with the amount it took off (F-POS-03, spec/06 §8.4). */
export const AppliedPromotionSchema = z
    .object({
    promotionId: PromotionIdSchema,
    name: z.string().min(1).max(120),
    amount: NonNegativeMoneySchema,
})
    .openapi('AppliedPromotion');
/**
 * Who a sale is billed to (F-CMP-07, spec/06 §10.2). A sale is **B2B** when the buyer gives a GSTIN, which is
 * validated by format and check character. Its first two digits are the buyer's state: when that differs from the
 * venue's (its own GSTIN's), the supply is **inter-state** and a `regular` venue charges IGST instead of CGST +
 * SGST (the same tax, not split; spec/06 §10.1). Both fields are null for a B2C sale.
 */
export const SaleBuyerSchema = z
    .object({
    /** The buyer's name as it goes on the tax invoice; null for a B2C sale. */
    name: z.string().nullable(),
    gstin: z.string().nullable(),
    /** `inter_state` for a B2B buyer registered in another state; `intra_state` otherwise. */
    placeOfSupply: z.enum(['intra_state', 'inter_state']),
})
    .openapi('SaleBuyer', { description: 'Who a sale is billed to: a B2B buyer’s name and GSTIN, and the place of supply (F-CMP-07).' });
/** Set a sale's B2B buyer before it is paid (F-CMP-07): their GSTIN and the name on the tax invoice. */
export const SetSaleBuyerSchema = z
    .object({
    gstin: GstinSchema,
    name: z.string().trim().min(1).max(120),
})
    .strict()
    .openapi('SetSaleBuyerRequest');
/** A sale (spec/06 §8): its lines, its tenders, its totals and its settlement. */
export const SaleSchema = z
    .object({
    id: SaleIdSchema,
    venueId: z.string(),
    /**
     * The order's human number, per venue per business day ("#0421", F-FNB-04): what the counter calls out and
     * the kitchen ticket, the kiosk tracker and Orders show. Allocated just after the sale is placed, so it is
     * null for a moment on a brand-new sale (and on a sale placed before order numbers).
     */
    orderNumber: z.int().min(1).nullable(),
    channel: SaleChannelSchema,
    status: SaleStatusSchema,
    taxMode: SaleTaxModeSchema,
    /** Who the sale is billed to (F-CMP-07): a B2B buyer's GSTIN and name, and the place of supply. */
    buyer: SaleBuyerSchema,
    playerId: PlayerIdSchema.nullable(),
    stationId: StationIdSchema.nullable(),
    sessionId: SessionIdSchema.nullable(),
    note: NOTE.nullable(),
    /**
     * "Scan & pay (UPI)" (F-PAY-02, F-POS-10, 2026-10-01): the player chose, at the kiosk, to pay this desk order by
     * scanning the venue's own UPI QR at the PC, with this short code as the payment's note. The queue marks it "UPI scan"
     * and staff confirm it with `counter_upi` once the payment is in. Absent for every other sale.
     */
    scanToPayReference: z.string().optional(),
    /**
     * `console_session`: the one order a staff-run session placed when it ended (ADR-0034) — its booked blocks and
     * controllers, priced from the rate card with console offers taken off, attached to the player when there is one.
     * It waits in the awaiting-payment queue ("Console session") until it is paid at the counter. Absent for every
     * other sale.
     */
    origin: z.enum(['console_session']).optional(),
    lines: z.array(SaleLineSchema),
    payments: z.array(SalePaymentSchema),
    /** The discounts applied to the sale — codes and manual (spec/06 §8.4). Empty for most sales. */
    discounts: z.array(SaleDiscountSchema),
    /** The automatic promotions applied when the sale was placed (F-POS-03, spec/06 §8.4). */
    promotions: z.array(AppliedPromotionSchema),
    /** The refunds against the sale (spec/06 §8.7). Empty until one is taken. */
    refunds: z.array(SaleRefundSchema),
    subtotal: MoneySchema,
    tax: MoneySchema,
    /** The total taken off the sale — discounts (codes/manual) and automatic promotions, derived (spec/06 §8.4). */
    discount: MoneySchema,
    /** `subtotal + tax − discount`: what the sale costs. */
    total: MoneySchema,
    /** The settled amount so far (sum of succeeded payments), derived (spec/06 §8.2). */
    paid: MoneySchema,
    /** The amount refunded so far (sum of succeeded refunds), derived (spec/06 §2.4 invariant 5). */
    refunded: MoneySchema,
    /** `total − paid`: what is still owed. Zero when the sale is paid. */
    outstanding: MoneySchema,
    /**
     * Whether the venue accepts a cash overpayment's change into the attached player's wallet (spec/06 §9): the wallet
     * is on and taxes top-ups at `redemption`. A venue capability, so the desk offers "Add to wallet" only here (and
     * only with a player on the sale and a cash overpayment); the server re-checks. Absent on reads that do not resolve
     * it — read as not available. Never changes the money on the sale.
     */
    changeToWalletAvailable: z.boolean().optional(),
    /**
     * Why the change cannot go to a wallet here, when `changeToWalletAvailable` is false: `wallet_off` (the organisation's
     * wallet, `wallet.enabled`, is off) or `top_ups_taxed_at_sale` (`tax.walletTopupTaxPoint` = `sale`). The desk says so
     * and points to the setting. Absent when available, and on reads that do not resolve the capability.
     */
    changeToWalletUnavailable: z.enum(['wallet_off', 'top_ups_taxed_at_sale']).optional(),
    placedAt: IsoDateTimeSchema,
    paidAt: IsoDateTimeSchema.nullable(),
    cancelledAt: IsoDateTimeSchema.nullable(),
})
    .openapi('Sale');
export const SaleListSchema = z.object({ sales: z.array(SaleSchema) }).openapi('SaleList');
/** The sale list's filter (F-POS-10): `awaiting=payment` is the pending kiosk/app orders and console sessions queue. */
export const SaleListQuerySchema = z
    .object({ awaiting: z.enum(['payment']).optional() })
    .openapi('SaleListQuery');
/**
 * The last delivery of a sale's document (F-POS-13, spec/06 §10.2): `queued` from the moment it is asked
 * for until the notifications provider answers, then `sent` or `failed`. `to` is the address **masked**
 * (`r•••@gmail.com`) — the document never stores or returns the full one. `auto` is the automatic receipt
 * on payment (`receipts.autoEmail`), rather than a staff member's send.
 */
export const SaleDocumentDeliverySchema = z
    .object({
    status: z.enum(['queued', 'sent', 'failed']),
    channel: z.enum(['email', 'whatsapp']),
    to: z.string().nullable(),
    auto: z.boolean(),
    requestedAt: IsoDateTimeSchema,
    /** When the provider answered; null while `queued`. */
    completedAt: IsoDateTimeSchema.nullable(),
    /** Why it failed, in words staff can act on (`no-address`, `provider-unavailable`, `timeout`…); null unless `failed`. */
    failure: z.string().nullable(),
})
    .openapi('SaleDocumentDelivery');
/** One line as a document prints it (spec/06 §10.2): HSN/SAC, quantity, rate, taxable value, tax components, total. */
export const SaleDocumentLineSchema = z
    .object({
    name: z.string(),
    /** The HSN or SAC code; null on a bill (it prints none) or when the category has none. */
    hsnSac: z.string().nullable(),
    quantity: z.int().min(1),
    /** The unit price with its modifiers, as the player saw it. */
    unitPrice: MoneySchema,
    /** The GST rate charged; zero at a venue that charges none. */
    rateBps: BasisPointsSchema,
    taxable: MoneySchema,
    tax: MoneySchema,
    taxComponents: z.array(SaleTaxComponentSchema),
    /** What the line costs: taxable + tax. */
    total: MoneySchema,
})
    .openapi('SaleDocumentLine');
/** One GST component summed over a document at one rate: what the invoice's tax summary prints. */
export const SaleDocumentTaxSummarySchema = z
    .object({
    component: z.enum(['CGST', 'SGST', 'IGST']),
    rateBps: BasisPointsSchema,
    /** The taxable value the component was charged on. */
    taxable: MoneySchema,
    amount: MoneySchema,
})
    .openapi('SaleDocumentTaxSummary');
/** A document's totals (spec/06 §10.2): the same figures on screen, in print, in the PDF view and in the email. */
export const SaleDocumentTotalsSchema = z
    .object({
    taxable: MoneySchema,
    tax: MoneySchema,
    /** `taxable + tax`, before any discount. */
    gross: MoneySchema,
    /** Promotions, codes and manual discounts, taken off the gross. */
    discount: MoneySchema,
    /** What was charged: `gross − discount`. */
    total: MoneySchema,
    taxSummary: z.array(SaleDocumentTaxSummarySchema),
    /**
     * Of `tax`, the GST already paid when the money that paid for the sale was topped up in the café's previous system
     * (a migration's tax-paid balance): not collected again. Absent when none.
     */
    taxPaidAtTopUp: MoneySchema.optional(),
})
    .openapi('SaleDocumentTotals');
/** One GST component a credit note reverses (F-CMP-07). */
export const CreditNoteTaxComponentSchema = z
    .object({ component: z.enum(['CGST', 'SGST', 'IGST']), amount: MoneySchema })
    .openapi('CreditNoteTaxComponent');
/** A document tender's change credited to a player's wallet, with the name the receipt prints (ADR-0049). */
export const SaleDocumentChangeToWalletSchema = z
    .object({
    amount: NonNegativeMoneySchema,
    playerId: PlayerIdSchema,
    /** The name the player goes by; null when the profile carries none. */
    playerName: z.string().nullable(),
})
    .openapi('SaleDocumentChangeToWallet');
/** One tender that paid a document's sale (spec/06 §10.2). */
export const SaleDocumentPaymentSchema = z
    .object({
    tender: SalePaymentTenderSchema,
    /** What the tender applied to the sale. */
    amount: MoneySchema,
    /** For cash: what the customer handed over, when it was entered; null otherwise (and absent on an older read). */
    tendered: NonNegativeMoneySchema.nullable().optional(),
    /** For cash: the change handed back from the drawer; null when none was recorded. */
    change: NonNegativeMoneySchema.nullable().optional(),
    /** For cash: the change credited to a player's wallet instead, with the name the receipt prints (ADR-0049). */
    changeToWallet: SaleDocumentChangeToWalletSchema.optional(),
})
    .openapi('SaleDocumentPayment');
/**
 * A credit note (spec/06 §10.2, F-CMP-07): issued for every settled refund of a sale that got a **tax invoice**,
 * numbered in its own series per GSTIN and financial year (`CN/2026-27/000001`) just after the refund commits. It
 * names the invoice it corrects and reverses the tax pro rata, exactly as the refund's ledger entry does. A refund
 * against a bill or a bill of supply gets none: no tax was collected (spec/06 §10.3 rule 8).
 */
export const CreditNoteSchema = z
    .object({
    id: CreditNoteIdSchema,
    refundId: SaleRefundIdSchema,
    series: z.string(),
    number: z.int().min(1),
    /** The human number, e.g. `CN/2026-27/000001`. */
    formattedNumber: z.string(),
    financialYear: z.string(),
    gstin: z.string().nullable(),
    /** The tax invoice it corrects. */
    invoiceNumber: z.string(),
    issuedAt: IsoDateTimeSchema,
    /** The taxable value reversed. */
    taxable: MoneySchema,
    /** The tax reversed. */
    tax: MoneySchema,
    taxComponents: z.array(CreditNoteTaxComponentSchema),
    /** What was refunded: taxable + tax − the discount released. */
    total: MoneySchema,
})
    .openapi('CreditNote');
/**
 * A sale's document (spec/06 §10.2, F-POS-13): the receipt a paid sale gets, titled by its tax mode —
 * a **bill** at an `unregistered` venue (the default), a **bill of supply** at a `composition` venue,
 * a **tax invoice** at a `regular` one. Numbered from a locked counter, per organisation (bills) or
 * per GSTIN (tax invoices) and financial year. The console renders it for print from the sale it
 * already holds; `send` delivers it (email today; the WhatsApp channel is a later provider).
 */
export const SaleDocumentSchema = z
    .object({
    id: SaleDocumentIdSchema,
    saleId: SaleIdSchema,
    kind: z.enum(['bill', 'bill_of_supply', 'tax_invoice']),
    title: z.string(),
    taxMode: SaleTaxModeSchema,
    series: z.string(),
    number: z.int().min(1),
    /** The human number, e.g. `BILL/2026-27/000045` (spec/06 §10.2). */
    formattedNumber: z.string(),
    gstin: z.string().nullable(),
    financialYear: z.string(),
    issuedAt: IsoDateTimeSchema,
    /** The channel the receipt was last requested on (`email` or `whatsapp`), and when; null until sent. */
    lastRequestedChannel: z.string().nullable(),
    lastRequestedAt: IsoDateTimeSchema.nullable(),
    /** The last delivery and its outcome (F-POS-13); null until one is asked for. */
    delivery: SaleDocumentDeliverySchema.nullable(),
    /** The venue the sale was made at, as the document heads it. */
    venueName: z.string(),
    /** Who it is billed to (F-CMP-07): a B2B buyer's name and GSTIN, and the place of supply. */
    buyer: SaleBuyerSchema,
    /** The lines as the document prints them (spec/06 §10.2). */
    lines: z.array(SaleDocumentLineSchema),
    totals: SaleDocumentTotalsSchema,
    /** The tenders that paid it. */
    payments: z.array(SaleDocumentPaymentSchema),
    /** The credit notes issued against it for refunds of its taxed lines (F-CMP-07); empty for most. */
    creditNotes: z.array(CreditNoteSchema),
})
    .openapi('SaleDocument');
// ---------------------------------------------------------------------------------------------
// Placing a sale (spec/06 §8.3)
// ---------------------------------------------------------------------------------------------
/**
 * One line a caller wants to sell.
 *
 * A `food` or `retail` line names a `productId` on the venue's effective menu (with an optional
 * `variantId` and chosen `modifierIds`); the server prices it from the menu and ignores `name`,
 * `unitPrice` and `taxRateBps`. A `time` or `other` line carries its own `name` and `unitPrice`; the server
 * decides its tax rate (the venue's rate for a custom line, never the client's) and computes its tax and totals.
 * `taxRateBps` is **ignored** on every line (2026-10-01): a client never chooses the tax a sale collects.
 *
 * A **pass line** (F-POS-01, F-PRC-01) is a `time` line naming a `passProductId`, of quantity 1 (one line is
 * one holding): the server prices it from the pass product's live version, untaxed (spec/06 §10.3 rule 2),
 * and ignores `name`, `unitPrice` and `taxRateBps`. The product must be sold at the counter (its channels),
 * inside its sale window, in scope for the venue, open to the player (members-only passes are not sold at the
 * counter yet) and within its purchase limits. The sale must name its `playerId` — the pass is theirs — and
 * be a `counter` sale. The holding is issued when the sale is paid; a cancelled sale issues none.
 *
 * A **guest pass line** (F-SES-07, spec/05 §3.4, ADR-0059) is a pass line with `guest: true`: the same pass product,
 * sold to a walk-in with no account. Only where the venue serves guests (`sessions.guestsEnabled`, else
 * `409 guests-off`), on a counter sale that names **no** player and no session — so it can be paid by cash, card or
 * counter UPI and never from a wallet or on account. A members-only pass is not sold to a guest; a per-player purchase
 * limit has no player to count against. Placing the sale mints the guest (`gst_…`) and its 6-character ticket code
 * (`GET …/guest-passes?saleId=`); the code starts play only once the sale is paid, which issues the guest's holding.
 */
export const CreateSaleLineSchema = z
    .object({
    kind: SaleLineKindSchema,
    passProductId: PassProductIdSchema.optional(),
    /** A guest pass line: the pass named by `passProductId`, for a walk-in with no account (see above). */
    guest: z.boolean().optional(),
    productId: ProductIdSchema.optional(),
    variantId: ProductVariantIdSchema.optional(),
    modifierIds: z.array(ProductModifierIdSchema).max(50).optional(),
    name: NAME.optional(),
    unitPrice: NonNegativeMoneySchema.optional(),
    /** Ignored (2026-10-01): the server decides every line's tax rate. Kept so an older client's body still parses. */
    taxRateBps: BasisPointsSchema.optional(),
    quantity: z.int().min(1).max(999),
})
    .openapi('CreateSaleLine');
/** Create a sale for the counter (spec/06 §8.3). The server prices it from the venue's menu and mode. */
export const CreateSaleSchema = z
    .object({
    channel: SaleChannelSchema.optional(),
    playerId: PlayerIdSchema.optional(),
    stationId: StationIdSchema.optional(),
    sessionId: SessionIdSchema.optional(),
    note: NOTE.optional(),
    lines: z.array(CreateSaleLineSchema).min(1).max(200),
    /**
     * The total the client was shown (its last quote), compared — never used as a price (spec/08 §2.9, F-SES-17
     * rule 3): when the server's total differs the sale is not placed, and the answer is `409 price-changed` with
     * the fresh `total`. Absent: no comparison.
     */
    expectedTotal: MoneySchema.optional(),
    /** A B2B buyer (F-CMP-07): their GSTIN and name. An out-of-state GSTIN makes a `regular` venue charge IGST. */
    buyer: SetSaleBuyerSchema.optional(),
})
    .openapi('CreateSaleRequest');
// ---------------------------------------------------------------------------------------------
// Quoting a sale before it is placed (F-POS-03, F-POS-02; spec/06 §8.4)
// ---------------------------------------------------------------------------------------------
/** A discount code as entered on a quote: the console's bounds; matched case-insensitively. */
const QUOTE_DISCOUNT_CODE = z.string().min(4).max(24);
/**
 * Price a prospective cart without placing it: the same lines `createSale` takes, and optionally a discount code.
 * Nothing is written, no code use is reserved and no stock moves.
 */
export const QuoteSaleSchema = z
    .object({
    ...CreateSaleSchema.omit({ expectedTotal: true }).shape,
    /** A discount code to include (F-POS-02): checked as `applyDiscountCode` checks it, but no use is reserved. */
    discountCode: QUOTE_DISCOUNT_CODE.optional(),
})
    .openapi('QuoteSaleRequest');
/** One quoted line: what it costs before any discount, what the automatic promotions take off it, and what is left. */
export const SaleQuoteLineSchema = z
    .object({
    name: NAME,
    quantity: z.int().min(1),
    /** The line's value as priced — its price, modifiers, quantity and tax (spec/06 §8.1). */
    gross: MoneySchema,
    /** The promotion discount allocated to this line (pro rata by gross, spec/06 §8.4 rule 2). */
    promotionDiscount: MoneySchema,
})
    .openapi('SaleQuoteLine');
/** A discount code included in a quote, and what it takes off after the promotions (spec/06 §8.4). */
export const SaleQuoteCodeSchema = z
    .object({
    /** The code, upper-cased as it is stored. */
    code: z.string(),
    amount: MoneySchema,
})
    .openapi('SaleQuoteCode');
/**
 * What a cart would cost if it were placed now (F-POS-03, spec/06 §8.4): the same pricing path `createSale` runs —
 * the menu, the tax mode, the automatic promotions in the venue's local time — and, when a code is included, the
 * same arithmetic `applyDiscountCode` runs on the placed sale. A quote reserves nothing; the sale placed from the
 * same cart a moment later is priced afresh and costs `total` unless a promotion's window closed in between.
 */
export const SaleQuoteSchema = z
    .object({
    lines: z.array(SaleQuoteLineSchema),
    /** What the lines cost before any discount (`subtotal + tax` of the sale). */
    gross: MoneySchema,
    /**
     * The GST in `gross` (F-PAY-11): zero at a venue that charges none, and on untaxed lines — a pass is untaxed unless
     * the café turned GST on passes on (`tax.playTaxed`). The kiosk shows it as "Includes GST".
     */
    tax: MoneySchema,
    /** The automatic promotions that apply, each with what it takes off. */
    promotions: z.array(AppliedPromotionSchema),
    /** The discount code included, and what it takes off; null when none was sent. */
    discountCode: SaleQuoteCodeSchema.nullable(),
    /** Everything taken off: the promotions plus the code. */
    discount: MoneySchema,
    /** `gross − discount`: what the sale would cost. Never below zero. */
    total: MoneySchema,
})
    .openapi('SaleQuote', { description: 'A cart priced with its promotions and code, before it is placed (F-POS-03).' });
/**
 * Take a tender against a sale (spec/06 §8.3 step 4). Each amount is at most the outstanding amount;
 * split across several payments is allowed (F-POS-12). For cash, `tendered` is what the player handed
 * over, so the server can return the change.
 */
export const TakeSalePaymentSchema = z
    .object({
    /**
     * `counter_card` is the venue's own card machine: `reference` may carry the last 4 digits and the slip's
     * reference, never a card number (F-PAY-02). `counter_upi` is recorded only once staff confirm the UPI app
     * approved it: the console shows the venue's QR (`payments.counterUpiVpa`) and waits for that confirmation.
     */
    tender: SaleTenderSchema,
    amount: NonNegativeMoneySchema,
    tendered: NonNegativeMoneySchema.optional(),
    /**
     * Cash overpay only (spec/06 §9): with `true`, and `tendered` over `amount`, the change is credited to the sale's
     * attached player's wallet as a **no-bonus cash top-up** in the same transaction, instead of handed back — the
     * drawer keeps all the cash and `wallet_cash` rises by the change. Available only at a venue whose wallet is on and
     * taxes top-ups at `redemption` (the default); refused `409 change-to-wallet-unavailable` otherwise, or for a
     * non-cash tender, a sale with no player, or no overpayment. Absent or `false` (the default): the change is given
     * back in cash, behaviour unchanged.
     */
    changeToWallet: z.boolean().optional(),
    /**
     * With `changeToWallet`, the player whose wallet takes the change when **the sale names no player** (ADR-0049): staff
     * pick them at checkout. The sale stays as it was (no player is attached to it; its price and tax do not move); only
     * the change's top-up names this player. Must be a player of the organisation (`404` otherwise). On a sale that names
     * a player it must be absent or that same player, else `409 change-to-wallet-unavailable`. Ignored without
     * `changeToWallet`.
     */
    changeToWalletPlayerId: PlayerIdSchema.optional(),
    reference: z.string().min(1).max(120).optional(),
})
    .openapi('TakeSalePaymentRequest');
// ---------------------------------------------------------------------------------------------
// Routes (venue-scoped)
// ---------------------------------------------------------------------------------------------
const SaleParamsSchema = VenueScopeParamsSchema.extend({ saleId: SaleIdSchema }).openapi('SaleParams');
export const createSaleRoute = createRoute({
    method: 'post',
    path: '/v1/organizations/{organizationId}/venues/{venueId}/sales',
    operationId: 'createSale',
    tags: ['commerce'],
    summary: 'Create a sale (F-POS-09, F-POS-01)',
    description: 'Prices a mixed basket of lines (time, food, retail, other) from the venue’s effective menu and ' +
        'tax mode, and places the sale. Does not take payment — that is a separate step (F-POS-12). ' +
        'A pass line (`passProductId`) is priced from the pass product’s live version and needs the sale’s ' +
        '`playerId`; its holding is issued when the sale is paid (F-PRC-01). Requires `orders:create`. Audited. ' +
        'Send an `Idempotency-Key`: a retry with it answers the sale already placed and places no second one.',
    [PERMISSION_EXTENSION]: ORDERS_CREATE,
    request: {
        params: VenueScopeParamsSchema,
        headers: IdempotencyHeaderSchema,
        body: { content: { 'application/json': { schema: CreateSaleSchema } }, required: true },
    },
    responses: {
        201: jsonResponse(SaleSchema, 'The placed sale, with its priced lines and totals.'),
        400: commonProblemResponses[400],
        401: commonProblemResponses[401],
        403: commonProblemResponses[403],
        404: problemResponse('No such venue, or a product is not on this venue’s menu.', 'not-found'),
        409: problemResponse('A product is sold out, a pass is not sold at the counter now (channel, sale window, members-only, purchase limit), a line is missing what its kind needs, the total differs from `expectedTotal` (`price-changed`, with the fresh `total`), or a guest pass line was sold where the venue serves no guests (`guests-off`).', 'conflict', 'price-changed', 'guests-off'),
        422: problemResponse('The same Idempotency-Key was already used for a different request.', 'idempotency-key-reused'),
        429: commonProblemResponses[429],
        500: commonProblemResponses[500],
    },
});
export const quoteSaleRoute = createRoute({
    method: 'post',
    path: '/v1/organizations/{organizationId}/venues/{venueId}/sales/quote',
    operationId: 'quoteSale',
    tags: ['commerce'],
    summary: 'Price a cart with its promotions before placing it (F-POS-03)',
    description: 'Prices a prospective cart exactly as `createSale` would — the venue’s menu, tax mode and automatic promotions ' +
        '(in the venue’s local time) — and, when `discountCode` is sent, what the code takes off after the promotions. ' +
        'Nothing is written and no code use is reserved: the Sell cart shows the promotion lines live before the sale is ' +
        'placed. Requires `orders:create`.',
    [PERMISSION_EXTENSION]: ORDERS_CREATE,
    request: {
        params: VenueScopeParamsSchema,
        body: { content: { 'application/json': { schema: QuoteSaleSchema } }, required: true },
    },
    responses: {
        200: jsonResponse(SaleQuoteSchema, 'The cart priced, with its promotions and code.'),
        400: commonProblemResponses[400],
        401: commonProblemResponses[401],
        403: commonProblemResponses[403],
        404: problemResponse('No such venue, a product is not on this venue’s menu, or no such discount code.', 'not-found', 'discount-code-unknown'),
        409: problemResponse('A product is sold out, a pass is not sold at the counter now, a line is missing what its kind needs, or the code does not apply.', 'conflict', 'discount-code-inactive', 'discount-code-expired', 'discount-code-minimum', 'discount-limit-reached'),
        429: commonProblemResponses[429],
        500: commonProblemResponses[500],
    },
});
export const listSalesRoute = createRoute({
    method: 'get',
    path: '/v1/organizations/{organizationId}/venues/{venueId}/sales',
    operationId: 'listSales',
    tags: ['commerce'],
    summary: 'List a venue’s sales (F-POS-15)',
    description: 'The venue’s sales, newest first. `awaiting=payment` narrows it to the pending queue at the top of Sell (F-POS-10, ' +
        'spec/13 §4): kiosk and app orders that are placed and have taken no payment — "Pay at the counter" — oldest first.',
    [PERMISSION_EXTENSION]: ORDERS_READ,
    request: { params: VenueScopeParamsSchema, query: SaleListQuerySchema },
    responses: {
        200: jsonResponse(SaleListSchema, 'The venue’s sales, newest first.'),
        400: commonProblemResponses[400],
        401: commonProblemResponses[401],
        403: commonProblemResponses[403],
        404: problemResponse('No such venue in this organisation.', 'not-found'),
        429: commonProblemResponses[429],
        500: commonProblemResponses[500],
    },
});
export const getSaleRoute = createRoute({
    method: 'get',
    path: '/v1/organizations/{organizationId}/venues/{venueId}/sales/{saleId}',
    operationId: 'getSale',
    tags: ['commerce'],
    summary: 'Read one sale (F-POS-15)',
    [PERMISSION_EXTENSION]: ORDERS_READ,
    request: { params: SaleParamsSchema },
    responses: {
        200: jsonResponse(SaleSchema, 'The sale, with its lines, tenders and settlement.'),
        400: commonProblemResponses[400],
        401: commonProblemResponses[401],
        403: commonProblemResponses[403],
        404: problemResponse('No such sale at this venue.', 'not-found'),
        429: commonProblemResponses[429],
        500: commonProblemResponses[500],
    },
});
export const takeSalePaymentRoute = createRoute({
    method: 'post',
    path: '/v1/organizations/{organizationId}/venues/{venueId}/sales/{saleId}/payments',
    operationId: 'takeSalePayment',
    tags: ['commerce'],
    summary: 'Take a tender against a sale (F-POS-12)',
    description: 'Records a cash, counter-UPI or wallet payment; split across several tenders is allowed, each at ' +
        'most the outstanding amount. A wallet payment draws the sale player’s wallet at once (bonus and ' +
        'cash in `wallet.spendOrder`) and needs a sale with a player and `wallet.enabled`. On the payment ' +
        'that covers the total, the sale settles to the ledger; its document (bill, bill of supply or tax invoice) is ' +
        'numbered and issued just after the payment commits, off the checkout’s critical path (spec/06 §10.2). A ' +
        '`reference` holding what looks like a whole card number is refused `422 card-number-refused`: enter only the ' +
        'last 4 digits. Requires `orders:create`. Audited. Send an `Idempotency-Key`: a retry with it answers the ' +
        'payment already taken and takes nothing twice.',
    [PERMISSION_EXTENSION]: ORDERS_CREATE,
    request: {
        params: SaleParamsSchema,
        headers: IdempotencyHeaderSchema,
        body: { content: { 'application/json': { schema: TakeSalePaymentSchema } }, required: true },
    },
    responses: {
        200: jsonResponse(SaleSchema, 'The sale after the payment, with its new status and change due.'),
        400: commonProblemResponses[400],
        401: commonProblemResponses[401],
        403: commonProblemResponses[403],
        404: problemResponse('No such sale at this venue, or no such player for the change (`changeToWalletPlayerId`).', 'not-found'),
        409: problemResponse('The sale is cancelled or already paid, the amount exceeds the balance, the wallet is off or does not hold enough, cash is taken by someone holding no open drawer (ADR-0031), or a cash overpayment cannot be sent to the wallet (not cash, no player on the sale or picked for the change, a different player from the sale’s, no overpayment, or the venue does not accept it) or would take the wallet over its limits.', 'conflict', 'cash-needs-shift', 'wallet-disabled', 'wallet-insufficient-funds', 'change-to-wallet-unavailable', 'wallet-limit'),
        422: problemResponse('The reference looks like a whole card number (enter only the last 4 digits), or the same Idempotency-Key was already used for a different request.', 'card-number-refused', 'idempotency-key-reused'),
        429: commonProblemResponses[429],
        500: commonProblemResponses[500],
    },
});
// ---------------------------------------------------------------------------------------------
// Retail: barcode lookup at Sell (F-POS-11; spec/13 §4)
// ---------------------------------------------------------------------------------------------
/**
 * A retail product found by its barcode (F-POS-11). A USB or Bluetooth scanner types the barcode
 * into the Sell search field; this resolves it to the product to add to the cart (spec/13 §4). The
 * product lives on the venue's effective menu (the `food` module owns it; retail reuses `product`).
 */
export const RetailProductSchema = z
    .object({
    productId: ProductIdSchema,
    name: NAME,
    barcode: z.string().min(1).max(64),
    unitPrice: NonNegativeMoneySchema,
    taxRateBps: BasisPointsSchema,
    /** Whether the product is available at this venue right now (spec/13 §6.1). */
    available: z.boolean(),
})
    .openapi('RetailProduct');
export const RetailLookupQuerySchema = z
    .object({ barcode: z.string().min(1).max(64) })
    .openapi('RetailLookupQuery');
export const lookupRetailProductRoute = createRoute({
    method: 'get',
    path: '/v1/organizations/{organizationId}/venues/{venueId}/retail/lookup',
    operationId: 'lookupRetailProduct',
    tags: ['commerce'],
    summary: 'Look up a retail product by barcode (F-POS-11)',
    description: 'Resolves a scanned barcode to a retail product on the venue’s effective menu, to add to the ' +
        'Sell cart. Requires `orders:read`.',
    [PERMISSION_EXTENSION]: ORDERS_READ,
    request: { params: VenueScopeParamsSchema, query: RetailLookupQuerySchema },
    responses: {
        200: jsonResponse(RetailProductSchema, 'The retail product the barcode names.'),
        400: commonProblemResponses[400],
        401: commonProblemResponses[401],
        403: commonProblemResponses[403],
        404: problemResponse('No such venue, or no product with that barcode here.', 'not-found'),
        429: commonProblemResponses[429],
        500: commonProblemResponses[500],
    },
});
export const cancelSaleRoute = createRoute({
    method: 'post',
    path: '/v1/organizations/{organizationId}/venues/{venueId}/sales/{saleId}/cancel',
    operationId: 'cancelSale',
    tags: ['commerce'],
    summary: 'Cancel a sale (F-POS-15)',
    description: 'Cancels (voids) a sale that has taken no payment. A sale whose business day has been closed can no longer be ' +
        'voided (spec/13 §10, F-OPS-06): the answer is 409 and a correction is a refund on a later day. Requires ' +
        '`orders:void`. Audited.',
    [PERMISSION_EXTENSION]: ORDERS_VOID,
    request: { params: SaleParamsSchema },
    responses: {
        200: jsonResponse(SaleSchema, 'The cancelled sale.'),
        400: commonProblemResponses[400],
        401: commonProblemResponses[401],
        403: commonProblemResponses[403],
        404: problemResponse('No such sale at this venue.', 'not-found'),
        409: problemResponse('The sale has taken a payment, is already cancelled, or its business day is closed.', 'conflict'),
        429: commonProblemResponses[429],
        500: commonProblemResponses[500],
    },
});
// ---------------------------------------------------------------------------------------------
// Discounts (F-POS-02 codes, F-POS-05 manual), applied to a placed, unpaid sale (spec/06 §8.4)
// ---------------------------------------------------------------------------------------------
/** Apply a discount code entered at checkout (spec/06 §8.4). At most one code per sale. */
export const ApplyDiscountCodeSchema = z
    .object({ code: z.string().min(4).max(24) })
    .openapi('ApplyDiscountCodeRequest');
/**
 * Apply a manual staff discount to a sale (spec/06 §8.4, F-POS-05). `percent` gives `percentBps`
 * (basis points off), `amount` gives a money amount; either is capped by the member's effective
 * limit of `pos.manualDiscountMaxBps`, and a `reason` is required and audited.
 */
export const ApplyManualDiscountSchema = z
    .object({
    type: z.enum(['percent', 'amount']),
    percentBps: BasisPointsSchema.optional(),
    amount: NonNegativeMoneySchema.optional(),
    reason: z.string().min(1).max(500),
})
    .openapi('ApplyManualDiscountRequest');
export const applyDiscountCodeRoute = createRoute({
    method: 'post',
    path: '/v1/organizations/{organizationId}/venues/{venueId}/sales/{saleId}/discount-code',
    operationId: 'applyDiscountCode',
    tags: ['commerce'],
    summary: 'Apply a discount code to a sale (F-POS-02)',
    description: 'Applies an organisation discount code to a placed, unpaid sale and re-prices it. At most one ' +
        'code per sale; a code cannot be applied after a payment (spec/06 §8.4 rule 4). Requires ' +
        '`orders:create`. Audited.',
    [PERMISSION_EXTENSION]: ORDERS_CREATE,
    request: {
        params: SaleParamsSchema,
        body: { content: { 'application/json': { schema: ApplyDiscountCodeSchema } }, required: true },
    },
    responses: {
        200: jsonResponse(SaleSchema, 'The sale with the discount applied and its new total.'),
        400: commonProblemResponses[400],
        401: commonProblemResponses[401],
        403: commonProblemResponses[403],
        404: problemResponse('No such sale at this venue, or no such code.', 'not-found'),
        409: problemResponse('The sale is paid or cancelled, a code is already applied, the code is inactive or not valid here, a per-player code has no player on the sale, or the code (or this player) has used up its limit.', 'conflict', 'discount-limit-reached'),
        429: commonProblemResponses[429],
        500: commonProblemResponses[500],
    },
});
export const applyManualDiscountRoute = createRoute({
    method: 'post',
    path: '/v1/organizations/{organizationId}/venues/{venueId}/sales/{saleId}/discount',
    operationId: 'applyManualDiscount',
    tags: ['commerce'],
    summary: 'Apply a manual staff discount to a sale (F-POS-05)',
    description: 'Applies a percentage or amount discount to a placed, unpaid sale, capped by the member’s ' +
        'effective limit of `pos.manualDiscountMaxBps` (spec/07 §6.4). A reason is required. Requires ' +
        '`orders:discount`. Audited.',
    [PERMISSION_EXTENSION]: ORDERS_DISCOUNT,
    request: {
        params: SaleParamsSchema,
        body: { content: { 'application/json': { schema: ApplyManualDiscountSchema } }, required: true },
    },
    responses: {
        200: jsonResponse(SaleSchema, 'The sale with the discount applied and its new total.'),
        400: commonProblemResponses[400],
        401: commonProblemResponses[401],
        403: problemResponse('Not allowed, or the discount is over the member’s cap.', 'forbidden', 'second-factor-required'),
        404: problemResponse('No such sale at this venue.', 'not-found'),
        409: problemResponse('The sale is paid or cancelled.', 'conflict'),
        429: commonProblemResponses[429],
        500: commonProblemResponses[500],
    },
});
// ---------------------------------------------------------------------------------------------
// Refunds (F-POS-08), partial with an approval path (spec/06 §8.7)
// ---------------------------------------------------------------------------------------------
/**
 * Refund a sale in whole or in part (spec/06 §8.7). Name **either** `lines` and quantities (the
 * server prices the refund from the sale) **or** an `amount`, plus the `tender` to refund to and a
 * `reason`. Above the effective limit of `pos.refundApprovalAmount` the refund waits for a second
 * member to approve (spec/07 §6.4).
 *
 * **Refund to the player's wallet** (2026-10-02): `tender: wallet` is a destination for a sale paid by
 * any tender — cash, counter card, counter UPI, online or a split. The money is credited to the
 * player's wallet **cash** (refundable later under `wallet.cashRefundsAllowed`); nothing leaves the
 * drawer. It needs `wallet.enabled` and a registered player on the sale; a sale with none takes
 * `playerId` here, and staff attach that player to the sale. The part of the refund that is not
 * wallet money going back where it came from is held to `wallet.maxBalanceAmount` (`wallet-limit`).
 */
export const RefundSaleSchema = z
    .object({
    tender: SaleRefundTenderSchema,
    reason: z.string().min(1).max(500),
    amount: NonNegativeMoneySchema.optional(),
    lines: z.array(SaleRefundLineSchema).max(200).optional(),
    /**
     * Only with `tender: wallet`, for a sale that names no player: the registered player whose wallet
     * is credited, attached to the sale by this refund (audited). Refused on a sale that already names
     * a different player.
     */
    playerId: PlayerIdSchema.optional(),
})
    .openapi('RefundSaleRequest');
const RefundParamsSchema = SaleParamsSchema.extend({ refundId: SaleRefundIdSchema }).openapi('SaleRefundParams');
export const refundSaleRoute = createRoute({
    method: 'post',
    path: '/v1/organizations/{organizationId}/venues/{venueId}/sales/{saleId}/refunds',
    operationId: 'refundSale',
    tags: ['commerce'],
    summary: 'Refund a sale (F-POS-08)',
    description: 'Refunds a paid sale in whole or in part to a chosen tender, reversing the ledger. Above the ' +
        'member’s effective limit of `pos.refundApprovalAmount` the refund is created `pending_approval` ' +
        'and settles only when a second member approves it. `tender: wallet` refunds any sale to its ' +
        'player’s wallet cash (a sale with no player names one in `playerId`), with the wallet on and ' +
        'within `wallet.maxBalanceAmount`; the drawer is not debited. `tender: online` sends the money back to the online ' +
        'payment through the payment provider, up to what the sale was paid online and not yet refunded online ' +
        '(`409 conflict` otherwise); the provider refund is asked for once the refund settles, and its commission comes ' +
        'back pro rata (ADR-0061). Requires `orders:refund`. Audited.',
    [PERMISSION_EXTENSION]: ORDERS_REFUND,
    request: {
        params: SaleParamsSchema,
        body: { content: { 'application/json': { schema: RefundSaleSchema } }, required: true },
    },
    responses: {
        201: jsonResponse(SaleSchema, 'The sale with the refund recorded — settled, or pending approval.'),
        400: commonProblemResponses[400],
        401: commonProblemResponses[401],
        403: commonProblemResponses[403],
        404: problemResponse('No such sale at this venue.', 'not-found'),
        409: problemResponse('The sale is not paid, the amount exceeds what remains refundable (or, to `online`, what was paid online), the wallet is off, a refund to the wallet would take it over its maximum balance, or no payment provider is configured for an online refund.', 'conflict', 'wallet-disabled', 'wallet-limit', 'online-payments-unavailable'),
        429: commonProblemResponses[429],
        500: commonProblemResponses[500],
    },
});
export const approveSaleRefundRoute = createRoute({
    method: 'post',
    path: '/v1/organizations/{organizationId}/venues/{venueId}/sales/{saleId}/refunds/{refundId}/approve',
    operationId: 'approveSaleRefund',
    tags: ['commerce'],
    summary: 'Approve a pending refund (F-POS-08)',
    description: 'Approves a `pending_approval` refund and settles it to the ledger. The approver must be a ' +
        'different member from the one who requested it (spec/06 §8.7). Requires `orders:refund_approve`. ' +
        'Audited.',
    [PERMISSION_EXTENSION]: ORDERS_REFUND_APPROVE,
    request: { params: RefundParamsSchema },
    responses: {
        200: jsonResponse(SaleSchema, 'The sale with the refund settled.'),
        400: commonProblemResponses[400],
        401: commonProblemResponses[401],
        403: problemResponse('Not allowed, or the approver is the member who requested the refund.', 'forbidden', 'second-factor-required'),
        404: problemResponse('No such sale or refund at this venue.', 'not-found'),
        409: problemResponse('The refund is not pending approval, or it refunds to a wallet that is now off or would now go over its maximum balance.', 'conflict', 'wallet-disabled', 'wallet-limit'),
        429: commonProblemResponses[429],
        500: commonProblemResponses[500],
    },
});
// ---------------------------------------------------------------------------------------------
// Receipts (F-POS-13): the sale's document, print and send (spec/06 §10.2, spec/13 §4)
// ---------------------------------------------------------------------------------------------
/** Send a sale's document (spec/13 §4). Email today; WhatsApp is a later provider (F-POS-13). */
export const SendSaleDocumentSchema = z
    .object({
    channel: z.enum(['email', 'whatsapp']),
    /** Where to send it, when it should not go to the player's stored contact. An email address for `email`. */
    to: z.string().trim().min(3).max(320).optional(),
})
    .openapi('SendSaleDocumentRequest');
export const getSaleDocumentRoute = createRoute({
    method: 'get',
    path: '/v1/organizations/{organizationId}/venues/{venueId}/sales/{saleId}/document',
    operationId: 'getSaleDocument',
    tags: ['commerce'],
    summary: 'Read a sale’s document (F-POS-13)',
    description: 'The document a paid sale was issued — for print and to render on screen. Requires `orders:read`.',
    [PERMISSION_EXTENSION]: ORDERS_READ,
    request: { params: SaleParamsSchema },
    responses: {
        200: jsonResponse(SaleDocumentSchema, 'The sale’s document.'),
        400: commonProblemResponses[400],
        401: commonProblemResponses[401],
        403: commonProblemResponses[403],
        404: problemResponse('No such sale, or it has no document yet (it is not paid).', 'not-found'),
        429: commonProblemResponses[429],
        500: commonProblemResponses[500],
    },
});
export const sendSaleDocumentRoute = createRoute({
    method: 'post',
    path: '/v1/organizations/{organizationId}/venues/{venueId}/sales/{saleId}/document/send',
    operationId: 'sendSaleDocument',
    tags: ['commerce'],
    summary: 'Send a sale’s document (F-POS-13)',
    description: 'Sends the sale’s document by email — to `to`, or to the player’s stored address. The request is recorded ' +
        'and queued in the same transaction; the notifications provider sends it after the commit (F-NTF-03), and ' +
        'the document’s `delivery` moves from `queued` to `sent` or `failed`. WhatsApp has no provider yet and is ' +
        'refused with `409`. Requires `orders:read`. Audited.',
    [PERMISSION_EXTENSION]: ORDERS_READ,
    request: {
        params: SaleParamsSchema,
        body: { content: { 'application/json': { schema: SendSaleDocumentSchema } }, required: true },
    },
    responses: {
        200: jsonResponse(SaleDocumentSchema, 'The document, with the delivery queued.'),
        400: commonProblemResponses[400],
        401: commonProblemResponses[401],
        403: commonProblemResponses[403],
        404: problemResponse('No such sale, or it has no document yet.', 'not-found'),
        409: problemResponse('There is no address to send it to, or the channel has no provider yet (WhatsApp).', 'conflict'),
        429: commonProblemResponses[429],
        500: commonProblemResponses[500],
    },
});
// ---------------------------------------------------------------------------------------------
// B2B buyer and the printable document (F-CMP-07; spec/06 §10.1, §10.2)
// ---------------------------------------------------------------------------------------------
export const setSaleBuyerRoute = createRoute({
    method: 'put',
    path: '/v1/organizations/{organizationId}/venues/{venueId}/sales/{saleId}/invoice/gstin',
    operationId: 'setSaleBuyer',
    tags: ['commerce'],
    summary: 'Set a sale’s B2B buyer (F-CMP-07)',
    description: 'Records the buyer’s GSTIN (format and check character validated) and the name for the tax invoice, before the sale is ' +
        'paid. The GSTIN’s state against the venue’s decides the place of supply: at a `regular` venue a buyer registered in ' +
        'another state is charged IGST instead of CGST + SGST — the same tax, not split — so the lines’ components are ' +
        're-split and nothing else changes (spec/06 §10.1). Refused `409` once a payment has been taken. Requires ' +
        '`orders:create`. Audited.',
    [PERMISSION_EXTENSION]: ORDERS_CREATE,
    request: {
        params: SaleParamsSchema,
        body: { content: { 'application/json': { schema: SetSaleBuyerSchema } }, required: true },
    },
    responses: {
        200: jsonResponse(SaleSchema, 'The sale, billed to the buyer.'),
        400: commonProblemResponses[400],
        401: commonProblemResponses[401],
        403: commonProblemResponses[403],
        404: problemResponse('No such sale at this venue.', 'not-found'),
        409: problemResponse('The sale has taken a payment, or is cancelled.', 'conflict'),
        429: commonProblemResponses[429],
        500: commonProblemResponses[500],
    },
});
/** Which printable layout of a sale's document (spec/06 §10.2): an 80 mm receipt, or an A4 tax invoice. */
export const SaleDocumentLayoutSchema = z.enum(['receipt', 'a4']).openapi('SaleDocumentLayout', {
    description: 'An 80 mm thermal receipt, or an A4 page (the tax invoice to save as PDF).',
});
export const HTML_CONTENT_TYPE = 'text/html';
export const printSaleDocumentRoute = createRoute({
    method: 'get',
    path: '/v1/organizations/{organizationId}/venues/{venueId}/sales/{saleId}/document/print',
    operationId: 'printSaleDocument',
    tags: ['commerce'],
    summary: 'The printable document (F-CMP-07, F-POS-13)',
    description: 'The sale’s document as a self-contained HTML page with print CSS: `receipt` for an 80 mm thermal printer, `a4` for the ' +
        'tax invoice on a page, which the browser’s print dialog saves as PDF (spec/06 §10.2). The lines, tax components and ' +
        'totals are the ones `GET …/document` returns and the email carries, to the paise. Requires `orders:read`.',
    [PERMISSION_EXTENSION]: ORDERS_READ,
    request: { params: SaleParamsSchema, query: z.object({ layout: SaleDocumentLayoutSchema.optional() }) },
    responses: {
        200: { description: 'The document as an HTML page.', content: { [HTML_CONTENT_TYPE]: { schema: z.string() } } },
        400: commonProblemResponses[400],
        401: commonProblemResponses[401],
        403: commonProblemResponses[403],
        404: problemResponse('No such sale, or it has no document yet (it is not paid).', 'not-found'),
        429: commonProblemResponses[429],
        500: commonProblemResponses[500],
    },
});
// ---------------------------------------------------------------------------------------------
// Counter UPI: the venue's own QR (F-PAY-02; spec/06 §8.3, §9.1)
// ---------------------------------------------------------------------------------------------
/**
 * The venue's own UPI ID for counter UPI (F-PAY-02): Sell draws it as a QR for the amount due (`upi://pay`) when
 * staff choose UPI, and records the payment only once staff confirm the player's UPI app approved it. The money goes
 * straight to the venue. `vpa` is null when the venue has not set `payments.counterUpiVpa`: Sell then records counter
 * UPI with no QR, as before.
 */
export const CounterUpiSchema = z
    .object({
    vpa: z.string().nullable(),
    /** The name the UPI app shows the player: the venue's. */
    payeeName: z.string(),
})
    .openapi('CounterUpi', { description: 'The venue’s own UPI ID for the counter UPI QR (F-PAY-02).' });
export const getCounterUpiRoute = createRoute({
    method: 'get',
    path: '/v1/organizations/{organizationId}/venues/{venueId}/counter-upi',
    operationId: 'getCounterUpi',
    tags: ['commerce'],
    summary: 'The venue’s counter UPI QR details (F-PAY-02)',
    description: 'The venue’s own UPI ID (`payments.counterUpiVpa`) and payee name, for the QR Sell shows when staff choose UPI. The payment ' +
        'is recorded only after staff confirm it; the Platform never touches the money. Requires `orders:read`.',
    [PERMISSION_EXTENSION]: ORDERS_READ,
    request: { params: VenueScopeParamsSchema },
    responses: {
        200: jsonResponse(CounterUpiSchema, 'The venue’s counter UPI details.'),
        400: commonProblemResponses[400],
        401: commonProblemResponses[401],
        403: commonProblemResponses[403],
        404: problemResponse('No such venue in this organisation.', 'not-found'),
        429: commonProblemResponses[429],
        500: commonProblemResponses[500],
    },
});
//# sourceMappingURL=commerce.js.map